import type { ReactNode } from 'react';
import { EYEBROW } from './sections/cmsTheme.ts';

export interface SectionHeadingProps {
  eyebrow?: string;
  children: ReactNode;
  /** Ink for the h2 — the combination's `heading`. */
  color?: string;
  /** Accent color for the eyebrow — the combination's `eyebrow`. */
  eyebrowColor?: string;
  align?: 'left' | 'center';
  /** Extra classes on the <header> (e.g. a different bottom margin in a two-column layout). */
  className?: string;
}

/**
 * The section title block every CMS section leads with: an optional accent eyebrow above a
 * display h2.
 *
 * CMS v2 shrank both a long way. The eyebrow lost its mono face and wide tracking for a
 * semibold uppercase label in the combination's accent color, and the h2 dropped from a
 * poster-scale 4xl/6xl/7xl to a 30/40px editorial size — sections are dense now, and a
 * heading three times the body size was doing the work of a hero.
 */
export function SectionHeading({
  eyebrow, children, color = 'text-mf-black', eyebrowColor, align = 'left', className,
}: SectionHeadingProps) {
  return (
    <header className={`mb-6 md:mb-8 ${align === 'center' ? 'text-center' : ''}${className ? ` ${className}` : ''}`}>
      {eyebrow && (
        <div className={`${EYEBROW} ${eyebrowColor ?? color} mb-3`}>
          {eyebrow}
        </div>
      )}
      <h2 className={`font-display ${color} text-3xl md:text-[40px] leading-none tracking-[-0.02em]`}>
        {children}
      </h2>
    </header>
  );
}
